import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import api from "../../Component/axios_Interceptor/api";
import url from "../../Component/server";
import { live_info_type } from "../User/Live/Live";

interface ChatComponentProps {
  mesage: string; // mesage prop 추가
  all_visible?: boolean; // all_visible prop 추가
  live_info: live_info_type;
}

const Box = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow-y: auto; // 스크롤 추가
  width: 100%;
  -ms-overflow-style: none; // IE 및 Edge
  scrollbar-width: none; // Firefox
  &::-webkit-scrollbar {
    display: none; // Chrome, Safari, Opera
  }
`;
const ChatBox = styled.div`
  flex-direction: row;
  display: flex;
  align-items: flex-start; // 전체 요소 상단 정렬
  line-height: 30px;
  font-size: 16px;
`;
const NameBox = styled.div<{ $type: string }>`
  margin-right: 15px;
  white-space: nowrap;
  align-self: flex-start; // 이름 박스 상단 정렬
  color: ${(props) =>
    props.$type === "manage"
      ? "rgba(255, 220, 106, 0.6)"
      : props.$type === "System"
      ? "rgba(235, 0, 59, 0.6)"
      : props.$type === "buy"
      ? "rgba(39, 104, 255, 0.6)"
      : "rgba(255, 255, 255, 0.6)"};
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 1); // 텍스트 그림자 추가
`;
const ContentBox = styled.div<{ $type: string }>`
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  word-break: break-all;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  align-self: flex-start; // 내용 박스 상단 정렬
  color: ${(props) =>
    props.$type === "manage"
      ? "rgba(255, 220, 106, 1)"
      : props.$type === "System"
      ? "rgba(235, 0, 59, 1)"
      : props.$type === "buy"
      ? "rgba(39, 104, 255, 1)"
      : "rgba(255, 255, 255, 1)"};
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 1); // 텍스트 그림자 추가
`;

const ChatComponent = forwardRef(
  ({ mesage, all_visible, live_info }: ChatComponentProps, ref) => {
    const [messages, setMessages] = useState<any[]>([]);
    const [token, setToken] = useState<string | null>(null);
    const [newMessage, setNewMessage] = useState<string>(mesage);
    const ws = useRef<WebSocket | null>(null);
    const sentMessageIds = useRef<Set<string>>(new Set());
    const boxRef = useRef<HTMLDivElement | null>(null); // 스크롤 박스를 참조할 ref 생성
    const [isAtBottom, setIsAtBottom] = useState(true); // 스크롤이 맨 아래에 있는지 여부

    useImperativeHandle(ref, () => ({
      sendMessage,
    }));

    const fetchToken_StreamInfo = async () => {
      try {
        const res = await api.post(url.Communication_url.createChatToken, {
          channelName: live_info?.channel_name,
        });
        // console.log("chat token", res);
        setToken(res.data.token);
      } catch (err) {
        console.log("실패", err);
      }
    };

    useEffect(() => {
      if (live_info) {
        fetchToken_StreamInfo();
      }
    }, [live_info]);

    useEffect(() => {
      const intervalId = setInterval(() => {
        fetchToken_StreamInfo(); // 2시간마다 실행
      }, 7200000);

      // 컴포넌트 언마운트 시 정리
      return () => clearInterval(intervalId);
    }, []);

    useEffect(() => {
      setNewMessage(mesage); // 새로운 mesage가 전달되면 상태 업데이트
    }, [mesage]);

    useEffect(() => {
      if (!token) return;

      if (ws.current) {
        ws.current.close();
      }

      const chatWebSocketUrl =
        "wss://edge.ivschat.ap-northeast-2.amazonaws.com";
      ws.current = new WebSocket(chatWebSocketUrl, token);

      ws.current.onopen = () => {
        // WebSocket 연결 성공

        if (ws.current && ws.current.readyState === WebSocket.OPEN) {
          setMessages((prevMessages) => [
            ...prevMessages,
            {
              Attributes: {
                type: "System",
              },
              Content: "채팅서버에 연결되었습니다.",
              Sender: {
                UserId: "System",
                Attributes: {
                  displayName: "System",
                },
              },
            },
          ]);
        }
      };

      ws.current.onmessage = (event) => {
        const data = JSON.parse(event.data.toString());
        if (!sentMessageIds.current.has(data.id)) {
          setMessages((prevMessages) => [...prevMessages, data]);
        }
      };

      ws.current.onerror = (error) => {
        // console.error("WebSocket 에러:", error);
      };

      ws.current.onclose = () => {
        // WebSocket 연결 종료 시 처리
      };

      return () => {
        ws.current?.close();
      };
    }, [token]);

    const handleScroll = () => {
      if (boxRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = boxRef.current;
        setIsAtBottom(scrollHeight - scrollTop - 200 < clientHeight);
      }
    };

    useEffect(() => {
      if (isAtBottom && boxRef.current) {
        boxRef.current.scrollTo({
          top: boxRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    }, [messages]); // 메시지가 변경될 때마다 실행

    const sendMessage = () => {
      if (
        ws.current &&
        ws.current.readyState === WebSocket.OPEN &&
        newMessage.trim()
      ) {
        const messageData = {
          action: "SEND_MESSAGE",
          content: newMessage,
          Attributes: { type: "user" },
        };
        ws.current.send(JSON.stringify(messageData));
        setNewMessage("");
      }
    };

    return (
      <Box ref={boxRef} onScroll={handleScroll}>
        {messages.map((message, index) => (
          <ChatBox key={index}>
            {all_visible && (
              <>
                <NameBox
                  $type={
                    message.Sender?.UserId === "master" ||
                    message.Sender?.UserId === "linkcampus" ||
                    message.Sender?.UserId === "testY" ||
                    message.Sender?.UserId === "system"
                      ? "manage"
                      : message.Attributes.type
                  }
                >
                  {message.Sender?.Attributes?.displayName ?? ""}
                </NameBox>
                <ContentBox
                  $type={
                    message.Sender?.UserId === "master" ||
                    message.Sender?.UserId === "linkcampus" ||
                    message.Sender?.UserId === "testY" ||
                    message.Sender?.UserId === "system"
                      ? "manage"
                      : message.Attributes.type
                  }
                >
                  {message.Content}
                </ContentBox>
              </>
            )}
          </ChatBox>
        ))}
      </Box>
    );
  }
);

export default ChatComponent;
